// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { SeriesKey } from '../types';
import { movers, splitMovers, flashNames, yearsIn, type Mover } from '../analysis';
import { DIVERGE } from '../colours';
import { escapeHtml, formatNumber } from '../utils/format';
import { segHtml, viewHead, wireNameRows } from './shared';

const SERIES_OPTS: Array<{ id: SeriesKey; label: string }> = [
  { id: 'NSW-boy', label: 'NSW boys' },
  { id: 'NSW-girl', label: 'NSW girls' },
  { id: 'QLD-boy', label: 'QLD boys' },
  { id: 'QLD-girl', label: 'QLD girls' },
];

function rankText(r: number | null): string {
  return r == null ? 'outside 100' : `#${r}`;
}

function moverRowsHtml(list: Mover[], maxAbs: number): string {
  return list
    .map((m) => {
      const climb = m.delta > 0;
      const w = (Math.abs(m.delta) / maxAbs) * 50; // % of half-track
      const fill = climb
        ? `left:50%;width:${w.toFixed(1)}%;background:${DIVERGE.up}`
        : `right:50%;width:${w.toFixed(1)}%;background:${DIVERGE.down}`;
      const tip = escapeHtml(`${m.name}: ${rankText(m.from)} → ${rankText(m.to)}`);
      return `<div class="mover-row" data-key="${escapeHtml(m.key)}" role="button" tabindex="0" data-tip="${tip}">
        <span class="mover-name">${escapeHtml(m.name)}</span>
        <span class="mover-track"><span class="mid"></span><span class="mover-fill" style="${fill}"></span></span>
        <span class="mover-delta ${climb ? 'up' : 'down'}">${climb ? '▲' : '▼'} ${Math.abs(m.delta)}</span>
      </div>`;
    })
    .join('');
}

export function renderMovers(root: HTMLElement, ctx: ViewContext): void {
  const { names } = ctx.data;
  let sk: SeriesKey = 'NSW-girl';
  let span = '10';

  root.innerHTML = `
    ${viewHead(
      'Risers & Fallers',
      'Which names climbed the rankings, and which slipped away. Choose a window: the past year, the past decade, or the whole record. A name entering from outside the top 100 counts as a rise from #101.',
    )}
    <div class="panel">
      <div class="controls">
        <span class="field-label">List:</span>
        ${segHtml('Series', SERIES_OPTS, sk)}
        <span class="field-label" style="margin-left:var(--space-md)">Over:</span>
        ${segHtml('Span', [
          { id: '1', label: '1 year' },
          { id: '10', label: '10 years' },
          { id: '25', label: '25 years' },
          { id: 'all', label: 'All time' },
        ], span)}
      </div>
      <p class="stat-note" data-role="window"></p>
      <div class="grid-2" style="margin-top:var(--space-md)">
        <div>
          <h3 style="color:var(--status-good);margin-bottom:var(--space-sm)">▲ Biggest risers</h3>
          <div data-role="risers"></div>
        </div>
        <div>
          <h3 style="color:var(--status-bad);margin-bottom:var(--space-sm)">▼ Biggest fallers</h3>
          <div data-role="fallers"></div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-title">Flash in the pan</div>
      <div class="panel-sub">Names that burned bright then vanished — a high peak in a single year, but only a handful of years in any top-100 list ever.</div>
      <div data-role="flash"></div>
    </div>
  `;

  const risersEl = root.querySelector('[data-role="risers"]') as HTMLElement;
  const fallersEl = root.querySelector('[data-role="fallers"]') as HTMLElement;
  const windowEl = root.querySelector('[data-role="window"]') as HTMLElement;

  function paint(): void {
    const years = yearsIn(names, sk);
    const toYear = years[years.length - 1];
    let fromYear: number;
    if (span === 'all') fromYear = years[0];
    else fromYear = Math.max(years[0], toYear - Number(span));
    // snap fromYear to an actual year present
    if (!years.includes(fromYear)) fromYear = years.find((y) => y >= fromYear) ?? years[0];

    windowEl.textContent = `${sk.replace('-', ' ')} · ${fromYear} → ${toYear}`;
    const all = movers(names, sk, fromYear, toYear);
    const { risers, fallers } = splitMovers(all, 12);
    const maxAbs = Math.max(1, ...all.map((m) => Math.abs(m.delta)));
    risersEl.innerHTML = risers.length ? moverRowsHtml(risers, maxAbs) : '<div class="empty">No risers in this window.</div>';
    fallersEl.innerHTML = fallers.length ? moverRowsHtml(fallers, maxAbs) : '<div class="empty">No fallers in this window.</div>';
  }

  // flash in the pan (whole dataset)
  const flash = flashNames(names, { maxYears: 8, minPeak: 120 }).slice(0, 12);
  (root.querySelector('[data-role="flash"]') as HTMLElement).innerHTML = flash.length
    ? `<div class="rank-list">${flash
        .map(
          (f, i) => `<div class="rank-row" data-key="${escapeHtml(f.key)}" role="button" tabindex="0">
            <span class="rank-pos">${i + 1}</span>
            <span class="rank-name">${escapeHtml(f.name)}</span>
            <span class="rank-count" style="width:auto;color:var(--text-tertiary)">peaked ${formatNumber(f.peak)} in ${f.peakYear}</span>
          </div>`,
        )
        .join('')}</div>`
    : '<div class="empty">None found.</div>';

  root.querySelectorAll('.seg').forEach((seg) => {
    const isSeries = seg.getAttribute('aria-label') === 'Series';
    seg.querySelectorAll('[data-opt]').forEach((b) =>
      b.addEventListener('click', () => {
        seg.querySelectorAll('[data-opt]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        const v = b.getAttribute('data-opt') as string;
        if (isSeries) sk = v as SeriesKey;
        else span = v;
        paint();
      }),
    );
  });

  wireNameRows(root, ctx.openName);
  paint();
}
